import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Col, Row} from "reactstrap";
import {Dropdown} from "../../../../../../components/base/dropdown/dropdown";
import {Tab, Tabs} from "../../../../../../components/base/Tabs";
import {getOffsetTop, withinVicinity} from "../../../../../../../core/services/utils/utils";
import TryAgain from "../../../../../../components/app-specific/try-again";
import Api from "../../../../../../../core/services/api/api";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {WarrantyAndReturnProcessSettingsScrollButton} from "../../../../../../components/app-specific/scroll_button";
import {WarrantyAndReturnTypes} from "../../../../../../../core/constants/enums";
import WarrantyAndReturnProcessSettingsTemplates from "./templates";
import WarrantyAndReturnProcessSettingsApplicability from "./support-applicability";
import WarrantyAndReturnProcessSettingsExtraFees from "./extra-fees";
import WarrantyAndReturnExtraFeesTitleManagementModal from "../../../../../../components/dialogs/part-support-extra-fees-title-management";
import useIndustry from "../../../../../../hooks/use-industry";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import ElementIds from "../../../../../../../core/models/element-ids";
import {useHistory, useLocation} from "react-router-dom";
import useQuery from "../../../../../../hooks/use-query";
import {stringifyUrl} from "query-string";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";

const WarrantyAndReturnProcessSettingTabs = {
    warranty: {
        id: WarrantyAndReturnTypes.warranty,
        name: "Warranty",
    },
    return: {
        id: WarrantyAndReturnTypes.return,
        name: "Return",
    },
}

const WarrantyAndReturnProcessSettingsScrollButtonSections = [
    {id: 'templates', name: 'Templates',},
    {id: 'support-applicability', name: 'Support Applicability',},
    {id: 'extra-fee', name: 'Extra Fee',},
];

const WarrantyAndReturnProcessSettings = () => {
    const history = useHistory()
    const location = useLocation();
    const [query] = useQuery()
    const {cachedIndustry, industries, categoriesOfFetchedIndustry, fetchedIndustry, fetchIndustry, hasNoIndustries} = useIndustry();
    const [loading, setLoading] = useState(false)
    const [data, setData] = useState({})
    const [scrolling, setScrolling] = useState(false);
    const [selectedSection, setSelectedSection] = useState();
    const [_openTitleManagement, _setOpenTitleManagement] = useState(false);
    const isMounted = useIsMounted()
    const {configureNotebookTags} = useConfigureNotebook('warranty-and-return-process-settings-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * Automatically fetches the cached industry when the component is mounted
     */
    useEffect(() => {
        if (!cachedIndustry?.id)
            return;
        void fetchIndustry({industryId: cachedIndustry.id});
    }, [cachedIndustry?.id, fetchIndustry]);

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.warrantyAndReturnSettingsProcess);
        configureBehavioralTool((tags) => tags.warrantyAndReturnSettingsProcess);
    }, [configureBehavioralTool, configureNotebookTags]);

    const categories = useMemo(() => categoriesOfFetchedIndustry.filter(e => !e.parentId) ?? [], [categoriesOfFetchedIndustry])
    const selectedCategory = useMemo(() => categories?.find(e => e.id === Number(query?.categoryId)), [categories, query?.categoryId])

    /**
     * Sets the categories of the industry to be used for selection.
     */
    useEffect(() => {
        if (query?.industryId) {
            fetchIndustry({industryId: query?.industryId}).then();
        }
    }, [query?.industryId, fetchIndustry]);

    /**
     * Sets the selected section as the user clicks on a section to scroll to.
     *
     * Gets the top of each section with respect to the client window (visible screen)
     * if there is a n element with a top of 0 or more, selects that section.
     *
     * @type {function(): undefined}
     */
    const onScroll = useCallback(() => {
        if (scrolling || !query?.categoryId) return;
        const sections = WarrantyAndReturnProcessSettingsScrollButtonSections;
        const mainHeightMid = Math.round(document.querySelector('main').getBoundingClientRect().height / 2);
        const tops = sections.map(e => document.getElementById(e.id).getBoundingClientRect().top);
        const middleMost = tops.findIndex(e => withinVicinity(e, mainHeightMid, 0, 90));
        if (middleMost === -1) {
            if (tops.find(e => e > 0) === -1) {
                // in case that a single top is visible, so that we must be at the bottom of window
                const lastSection = sections[sections.length - 1];
                if (lastSection !== selectedSection) setSelectedSection(lastSection)

            } else {
                // in case that none of the  tops are visible, so that we must be at the top or bottom of window
                let section = sections[0];
                if (Math.abs(tops[0]) > Math.abs(tops[tops.length - 1])) {
                    section = sections[sections.length - 1];
                }
                if (section !== selectedSection) setSelectedSection(section)
            }
        } else {
            const newSelected = sections[middleMost];
            if (newSelected !== selectedSection) setSelectedSection(newSelected)
        }
    }, [query?.categoryId, scrolling, selectedSection])

    /**
     * As soon as the component mounts:
     * fetches the categories required for the user selection.
     */
    useEffect(() => {
        if (query?.typeId)
            return
        history.replace(stringifyUrl({
            url: location.pathname,
            query: {
                categoryId: query?.categoryId,
                industryId: fetchedIndustry?.id,
                typeId: WarrantyAndReturnProcessSettingTabs.warranty.id,
            }
        }))
    }, [fetchedIndustry?.id, query?.categoryId, query?.typeId])

    /**
     * As soon as the component mounts:
     * - Attaches a scroll event listener for the main section of the website so that if the user scrolls, the proper
     *      section gets selected when the application search is in place.
     */
    useEffect(() => {
        const main = document.querySelector('main');
        main?.addEventListener('scroll', onScroll);
        return () => main?.removeEventListener('scroll', onScroll);
    }, [onScroll])

    /**
     * Listens for the changes in typeId and categoryId of the templates and with each change:
     * Fetches the list of templates for the specific type and category.
     */
    useEffect(() => {
        if (!query?.typeId || !query?.categoryId) return
        setLoading(true)
        getAllProcessData()
    }, [query?.typeId, query?.categoryId])

    /**
     * Fetches all the process data for the specific type and category.
     */
    const getAllProcessData = () => {
        const forApi = {
            categoryId: Number(query.categoryId),
            requestTypeId: Number(query.typeId),
        }
        Api.getWarrantyAndReturnProcessSettingData(forApi).then((response) => {
            if (!isMounted()) return
            if (response?.resultFlag) {
                setData(response.data)
            }
            setLoading(false)
        })
    }

    /**
     * Changes the selected category in the url query params.
     * @param {MouseEvent} e
     */
    const onSelectedCategoryChanged = (e) => {
        history.replace(stringifyUrl({
            url: location.pathname,
            query: {
                ...(query ?? {}),
                industryId: fetchedIndustry?.id,
                categoryId: e.target.value.id,
            }
        }))
    }

    /**
     * Changes the tab of the view.
     * @param event {MouseEvent}
     * @param newValue {number}
     */
    const onTabChange = (event, newValue) => {
        history.replace(stringifyUrl({
            url: location.pathname,
            query: {
                industryId: fetchedIndustry?.id,
                categoryId: query?.categoryId,
                typeId: newValue
            }
        }))
    };


    /**
     * Scrolls the user to the selected section.
     *
     * Gets the height of the topBar and scrolls the window to the top fo the selected section - the height of the
     * topBar so that nothing gets behind the top section
     * @param section
     */
    const scrollToSection = (section) => {
        setScrolling(true);
        const topBarHeight = document.getElementById(ElementIds.panel.appBar).getBoundingClientRect().height + 30;
        document.querySelector('main')?.scrollTo(0, getOffsetTop(document.getElementById(section?.id), ElementIds.panel.main) - topBarHeight)
        setScrolling(false);
    }

    /**
     * Syncs the titles from the modal with the data in the view.
     * @param {any[]} titles
     */
    const syncTitles = (titles) => {
        setData(prevState => ({
            ...prevState,
            partSupportReqtExtraFeeAvailableTitles: titles
        }))
    };

    return (
        <>
            <Row className='no-gutters w-100 align-items-start justify-content-center notifications-view'>
                <Col xs={12}>
                    <p className={'text-md font-weight-bold mt-4 mb-4'}>
                        Request Configuration
                    </p>
                </Col>
                {
                    industries.length > 1 &&
                    <Col xs={12} md={6} className={'mb-3 px-2'}>
                        <Dropdown
                            label={'Industry'}
                            value={fetchedIndustry}
                            data={industries?.map(e => ({value: e, label: e.title, id: e.id})) ?? []}
                            onChange={(e) => fetchIndustry({industryId: e.target.value?.id})}
                        />
                    </Col>
                }
                {
                    !hasNoIndustries &&
                    <Col xs={12} md={industries.length > 1 ? 6 : 12} className={'mb-3 px-2'}>
                        <Dropdown
                            label={'Category'}
                            value={selectedCategory}
                            data={categories?.map(e => ({value: e, label: e.title, id: e.id})) ?? []}
                            onChange={onSelectedCategoryChanged}
                        />
                    </Col>
                }

                <Col xs={12}/>
                {
                    hasNoIndustries &&
                    <TryAgain
                        text={"No industries found. Please add an industry or contact your administrator."}
                    />
                }
                {
                    !hasNoIndustries && (
                        query?.categoryId === undefined || query?.typeId === undefined
                            ? <>
                                <TryAgain
                                    text={"Please select a category to view and manage its process configurations."}
                                />
                            </>
                            : <>
                                <div className='w-100 flex-grow-1 part-support-process-view'>
                                    <Tabs
                                        value={Number(query?.typeId)}
                                        onChange={onTabChange}
                                        aria-label="Warranty and Return Process Tabs"
                                    >
                                        <Tab
                                            label={WarrantyAndReturnProcessSettingTabs.warranty.name}
                                            value={WarrantyAndReturnProcessSettingTabs.warranty.id}
                                        />
                                        <Tab
                                            label={WarrantyAndReturnProcessSettingTabs.return.name}
                                            value={WarrantyAndReturnProcessSettingTabs.return.id}
                                        />
                                    </Tabs>
                                    <div className='my-4 w-100'/>
                                    <WarrantyAndReturnProcessSettingsTemplates
                                        section={WarrantyAndReturnProcessSettingsScrollButtonSections[0]}
                                        fileTypes={data.partSupportReqImageTempsAvailableFileTypes ?? []}
                                        templates={data.partSupportReqImageTemps ?? []}
                                        loading={loading}
                                    />
                                    <div className='my-4 w-100'/>
                                    <WarrantyAndReturnProcessSettingsApplicability
                                        section={WarrantyAndReturnProcessSettingsScrollButtonSections[1]}
                                        applicability={data.partSupportReqApplicabilities ?? []}
                                        selectedCategory={selectedCategory}
                                        loading={loading}
                                        isWarranty={query?.typeId === WarrantyAndReturnProcessSettingTabs.warranty.id}
                                    />
                                    <div className='my-4 w-100'/>
                                    <WarrantyAndReturnProcessSettingsExtraFees
                                        section={WarrantyAndReturnProcessSettingsScrollButtonSections[2]}
                                        fees={data.partSupportReqExtraFees ?? []}
                                        loading={loading}
                                        titles={data.partSupportReqtExtraFeeAvailableTitles}
                                        openTitleManagement={() => _setOpenTitleManagement(true)}
                                    />
                                    <div className='my-5 w-100'/>
                                    <div
                                        className={`sticky-section d-flex flex-column justify-content-center align-items-center`}>
                                        {
                                            WarrantyAndReturnProcessSettingsScrollButtonSections.map(section => (
                                                <WarrantyAndReturnProcessSettingsScrollButton
                                                    key={section.id ?? -1}
                                                    section={section}
                                                    selected={section.id === selectedSection?.id}
                                                    onSelect={scrollToSection}
                                                />
                                            ))
                                        }
                                    </div>
                                </div>
                            </>
                    )
                }
            </Row>
            <WarrantyAndReturnExtraFeesTitleManagementModal
                open={_openTitleManagement}
                setOpen={_setOpenTitleManagement}
                setTitles={syncTitles}
            />
        </>
    )
}


export default WarrantyAndReturnProcessSettings;
